import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu as MenuIcon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { media } from "@/lib/api";
import { MobileNav } from "./MobileNav";

const linkCls = ({ isActive }) =>
  `px-3 py-2 text-[15px] transition-colors ${isActive ? "text-crimson" : "text-charcoal hover:text-crimson"}`;

const fade = {
  initial: { opacity: 0, y: -8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
};

const MegaPanel = ({ directory, onPick }) => (
  <motion.div {...fade} className="absolute left-0 right-0 top-full px-4" data-testid="mega-menu-panel">
    <div className="max-w-[1280px] mx-auto bg-white border-t-4 border-crimson rounded-b-xl shadow-2xl px-7 py-7 max-h-[75vh] overflow-y-auto">
      <div className="columns-5 gap-8">
        {directory.filter((c) => c.hotels.length).map((city) => (
          <div key={city.id} className="break-inside-avoid mb-6" data-testid={`mega-city-${city.slug}`}>
            <h4 className="font-sans font-bold uppercase text-[15px] text-crimson tracking-wide mb-2">{city.name}</h4>
            <ul className="space-y-1.5">
              {city.hotels.map((h) => (
                <li key={h.id}>
                  <Link to={`/hotel/${h.slug}`} onClick={onPick} className="text-sm text-stone-600 hover:text-crimson transition-colors leading-snug block" data-testid={`mega-hotel-${h.slug}`}>
                    {h.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </motion.div>
);

const Dropdown = ({ item, onPick }) => (
  <motion.div {...fade} className="absolute left-0 top-full pt-2 min-w-[240px]" data-testid={`dropdown-${item.id}`}>
    <div className="bg-white border border-stone-200 shadow-xl rounded-md py-2">
      {item.children.filter((c) => c.visible !== false).map((c) => (
        <NavLink key={c.id} to={c.url} onClick={onPick} className="block px-4 py-2.5 text-sm text-charcoal hover:bg-ivory hover:text-crimson transition-colors" data-testid={`nav-child-${c.id}`}>
          {c.label}
        </NavLink>
      ))}
    </div>
  </motion.div>
);

export const Header = () => {
  const { content, menu, directory } = useSite();
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const items = menu.filter((m) => m.visible !== false);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-stone-200" onMouseLeave={() => setOpen(null)} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <img src={media(content?.site?.logo)} alt="IHD Indian Hotel Directory" className="h-9 sm:h-11 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {items.map((item) =>
            item.type === "link" ? (
              <NavLink key={item.id} to={item.url} end={item.url === "/"} className={linkCls} onMouseEnter={() => setOpen(null)} data-testid={`nav-${item.id}`}>
                {item.label}
              </NavLink>
            ) : (
              <div key={item.id} className={item.type === "dropdown" ? "relative" : ""} onMouseEnter={() => setOpen(item.id)}>
                <button className={`px-3 py-2 text-[15px] flex items-center gap-1 transition-colors ${open === item.id ? "text-crimson" : "text-charcoal hover:text-crimson"}`} data-testid={`nav-${item.id}`} onClick={() => setOpen(open === item.id ? null : item.id)}>
                  {item.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${open === item.id ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>{open === item.id && item.type === "dropdown" && <Dropdown item={item} onPick={() => setOpen(null)} />}</AnimatePresence>
              </div>
            )
          )}
        </nav>
        <button className="lg:hidden p-2 text-charcoal" onClick={() => setMobile(true)} data-testid="mobile-menu-button" aria-label="Open menu">
          <MenuIcon className="w-6 h-6" />
        </button>
      </div>
      <AnimatePresence>{items.some((i) => i.type === "mega" && i.id === open) && <MegaPanel key="mega" directory={directory} onPick={() => setOpen(null)} />}</AnimatePresence>
      <MobileNav open={mobile} onOpenChange={setMobile} items={items} directory={directory} logo={media(content?.site?.logo)} />
    </header>
  );
};
